import { type Static, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";

export const WORKFORCE_ATTENDANCE_CONTRACT_VERSION = "WORKFORCE_ATTENDANCE_V1" as const;

const STABLE_ID_PATTERN = "^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$";
const IDEMPOTENCY_KEY_PATTERN = "^[A-Za-z0-9][A-Za-z0-9._:-]{7,127}$";
const BUSINESS_DATE_PATTERN = "^[0-9]{4}-[0-9]{2}-[0-9]{2}$";
const BUSINESS_DAY_CUTOVER_PATTERN = "^(?:[01][0-9]|2[0-3]):[0-5][0-9]$";
const SCHEDULE_NUMBER_PATTERN = /^PB-(\d{4})(\d{2})(\d{2})-([0-9A-F]{12})$/u;
const ATTENDANCE_NUMBER_PATTERN = /^KQ-(\d{4})(\d{2})(\d{2})-([0-9A-F]{12})$/u;
const CORRECTION_NUMBER_PATTERN = /^BK-(\d{4})(\d{2})(\d{2})-([0-9A-F]{12})$/u;
const LEAVE_REQUEST_NUMBER_PATTERN = /^QJ-(\d{4})(\d{2})(\d{2})-([0-9A-F]{12})$/u;
const BUSINESS_NUMBER_SOURCE_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._:-]{11,127}$/u;
const FNV_1A_64_OFFSET = 0xcbf29ce484222325n;
const FNV_1A_64_PRIME = 0x100000001b3n;
const LOWER_48_BITS = 0xffffffffffffn;

const StableIdSchema = Type.String({ minLength: 1, maxLength: 128, pattern: STABLE_ID_PATTERN });
const IdempotencyKeySchema = Type.String({ pattern: IDEMPOTENCY_KEY_PATTERN });
const CanonicalDateTimeSchema = Type.String({ minLength: 24, maxLength: 24 });
const NullableDateTimeSchema = Type.Union([CanonicalDateTimeSchema, Type.Null()]);
const VersionSchema = Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER });
const NullableVersionSchema = Type.Union([VersionSchema, Type.Null()]);
const BusinessDateSchema = Type.String({ pattern: BUSINESS_DATE_PATTERN });
const BusinessDayCutoverSchema = Type.String({ pattern: BUSINESS_DAY_CUTOVER_PATTERN });
const TimeZoneSchema = Type.String({ minLength: 1, maxLength: 80 });
const PersonNameSchema = Type.String({ minLength: 1, maxLength: 80 });
const EmploymentNumberSchema = Type.String({ minLength: 1, maxLength: 80 });
const ReasonSchema = Type.String({ minLength: 2, maxLength: 300 });

export const WorkforceAttendanceRoleSchema = Type.Union([
  Type.Literal("MANAGER"),
  Type.Literal("CASHIER"),
  Type.Literal("WAITER"),
  Type.Literal("COACH"),
]);
export type WorkforceAttendanceRole = Static<typeof WorkforceAttendanceRoleSchema>;

export const WorkforceScheduleNumberSchema = Type.String({
  pattern: "^PB-[0-9]{8}-[0-9A-F]{12}$",
});
export type WorkforceScheduleNumber = Static<typeof WorkforceScheduleNumberSchema>;

export const WorkforceAttendanceNumberSchema = Type.String({
  pattern: "^KQ-[0-9]{8}-[0-9A-F]{12}$",
});
export type WorkforceAttendanceNumber = Static<typeof WorkforceAttendanceNumberSchema>;

export const WorkforceAttendanceCorrectionNumberSchema = Type.String({
  pattern: "^BK-[0-9]{8}-[0-9A-F]{12}$",
});
export type WorkforceAttendanceCorrectionNumber = Static<
  typeof WorkforceAttendanceCorrectionNumberSchema
>;

export const WorkforceLeaveRequestNumberSchema = Type.String({
  pattern: "^QJ-[0-9]{8}-[0-9A-F]{12}$",
});
export type WorkforceLeaveRequestNumber = Static<typeof WorkforceLeaveRequestNumberSchema>;

export const WorkforceScheduleSchema = Type.Object(
  {
    contractVersion: Type.Literal(WORKFORCE_ATTENDANCE_CONTRACT_VERSION),
    scheduleId: StableIdSchema,
    scheduleNumber: WorkforceScheduleNumberSchema,
    scheduleVersion: VersionSchema,
    tenantId: StableIdSchema,
    storeId: StableIdSchema,
    businessDate: BusinessDateSchema,
    timeZone: TimeZoneSchema,
    businessDayCutover: BusinessDayCutoverSchema,
    employeeId: StableIdSchema,
    employeeNumber: EmploymentNumberSchema,
    employeeName: PersonNameSchema,
    role: WorkforceAttendanceRoleSchema,
    plannedStartAt: CanonicalDateTimeSchema,
    plannedEndAt: CanonicalDateTimeSchema,
    state: Type.Union([Type.Literal("SCHEDULED"), Type.Literal("CANCELLED")]),
    createdBy: StableIdSchema,
    cancellationReason: Type.Union([ReasonSchema, Type.Null()]),
    createdAt: CanonicalDateTimeSchema,
    updatedAt: CanonicalDateTimeSchema,
  },
  { additionalProperties: false },
);
export type WorkforceSchedule = Static<typeof WorkforceScheduleSchema>;

export const WorkforceAttendanceRecordSchema = Type.Object(
  {
    contractVersion: Type.Literal(WORKFORCE_ATTENDANCE_CONTRACT_VERSION),
    attendanceId: StableIdSchema,
    attendanceNumber: WorkforceAttendanceNumberSchema,
    attendanceVersion: VersionSchema,
    scheduleId: StableIdSchema,
    scheduleNumber: WorkforceScheduleNumberSchema,
    employeeId: StableIdSchema,
    state: Type.Union([Type.Literal("WORKING"), Type.Literal("COMPLETED")]),
    checkedInAt: CanonicalDateTimeSchema,
    checkedOutAt: NullableDateTimeSchema,
    lateMinutes: Type.Integer({ minimum: 0, maximum: 1_440 }),
    earlyLeaveMinutes: Type.Union([Type.Integer({ minimum: 0, maximum: 1_440 }), Type.Null()]),
    overtimeMinutes: Type.Union([Type.Integer({ minimum: 0, maximum: 1_440 }), Type.Null()]),
    lastCorrectionNumber: Type.Union([WorkforceAttendanceCorrectionNumberSchema, Type.Null()]),
    createdAt: CanonicalDateTimeSchema,
    updatedAt: CanonicalDateTimeSchema,
  },
  { additionalProperties: false },
);
export type WorkforceAttendanceRecord = Static<typeof WorkforceAttendanceRecordSchema>;

export const WorkforceAttendanceCorrectionSchema = Type.Object(
  {
    contractVersion: Type.Literal(WORKFORCE_ATTENDANCE_CONTRACT_VERSION),
    correctionId: StableIdSchema,
    correctionNumber: WorkforceAttendanceCorrectionNumberSchema,
    correctionVersion: VersionSchema,
    scheduleId: StableIdSchema,
    scheduleNumber: WorkforceScheduleNumberSchema,
    attendanceId: Type.Union([StableIdSchema, Type.Null()]),
    employeeId: StableIdSchema,
    state: Type.Union([
      Type.Literal("PENDING"),
      Type.Literal("DECIDED"),
      Type.Literal("ACKNOWLEDGED"),
    ]),
    decision: Type.Union([Type.Literal("APPROVED"), Type.Literal("REJECTED"), Type.Null()]),
    requestedCheckedInAt: CanonicalDateTimeSchema,
    requestedCheckedOutAt: Type.Union([CanonicalDateTimeSchema, Type.Null()]),
    requestReason: ReasonSchema,
    requestedBy: StableIdSchema,
    requestedAt: CanonicalDateTimeSchema,
    decisionNote: Type.Union([ReasonSchema, Type.Null()]),
    decidedBy: Type.Union([StableIdSchema, Type.Null()]),
    decidedAt: NullableDateTimeSchema,
    acknowledgedAt: NullableDateTimeSchema,
    createdAttendanceVersion: NullableVersionSchema,
  },
  { additionalProperties: false },
);
export type WorkforceAttendanceCorrection = Static<typeof WorkforceAttendanceCorrectionSchema>;

export const WorkforceLeaveRequestSchema = Type.Object(
  {
    contractVersion: Type.Literal(WORKFORCE_ATTENDANCE_CONTRACT_VERSION),
    leaveRequestId: StableIdSchema,
    leaveRequestNumber: WorkforceLeaveRequestNumberSchema,
    leaveRequestVersion: VersionSchema,
    scheduleId: StableIdSchema,
    scheduleNumber: WorkforceScheduleNumberSchema,
    employeeId: StableIdSchema,
    state: Type.Union([
      Type.Literal("PENDING"),
      Type.Literal("DECIDED"),
      Type.Literal("ACKNOWLEDGED"),
    ]),
    decision: Type.Union([Type.Literal("APPROVED"), Type.Literal("REJECTED"), Type.Null()]),
    requestReason: ReasonSchema,
    requestedBy: StableIdSchema,
    requestedAt: CanonicalDateTimeSchema,
    decisionNote: Type.Union([ReasonSchema, Type.Null()]),
    decidedBy: Type.Union([StableIdSchema, Type.Null()]),
    decidedAt: NullableDateTimeSchema,
    acknowledgedAt: NullableDateTimeSchema,
  },
  { additionalProperties: false },
);
export type WorkforceLeaveRequest = Static<typeof WorkforceLeaveRequestSchema>;

export const WorkforceAttendanceWorkspaceItemSchema = Type.Object(
  {
    revision: VersionSchema,
    schedule: WorkforceScheduleSchema,
    attendance: Type.Union([WorkforceAttendanceRecordSchema, Type.Null()]),
    latestCorrection: Type.Union([WorkforceAttendanceCorrectionSchema, Type.Null()]),
    latestLeaveRequest: Type.Optional(Type.Union([WorkforceLeaveRequestSchema, Type.Null()])),
  },
  { additionalProperties: false },
);
export type WorkforceAttendanceWorkspaceItem = Static<
  typeof WorkforceAttendanceWorkspaceItemSchema
>;

export const WorkforceAttendanceWorkspaceSchema = Type.Object(
  {
    contractVersion: Type.Literal(WORKFORCE_ATTENDANCE_CONTRACT_VERSION),
    tenantId: StableIdSchema,
    storeId: StableIdSchema,
    viewer: Type.Union([Type.Literal("SELF"), Type.Literal("MANAGER")]),
    viewerEmployeeId: Type.Union([StableIdSchema, Type.Null()]),
    businessDate: BusinessDateSchema,
    timeZone: TimeZoneSchema,
    businessDayCutover: BusinessDayCutoverSchema,
    items: Type.Array(WorkforceAttendanceWorkspaceItemSchema, { maxItems: 500 }),
    generatedAt: CanonicalDateTimeSchema,
  },
  { additionalProperties: false },
);
export type WorkforceAttendanceWorkspace = Static<typeof WorkforceAttendanceWorkspaceSchema>;

export type WorkforceAttendanceOutcome =
  | "SCHEDULED"
  | "WORKING"
  | "COMPLETED"
  | "ABSENT"
  | "LEAVE_PENDING"
  | "LEAVE_APPROVED_AWAITING_ACKNOWLEDGEMENT"
  | "LEAVE_REJECTED_AWAITING_ACKNOWLEDGEMENT"
  | "LEAVE_APPROVED"
  | "CANCELLED";

export const CreateWorkforceScheduleRequestSchema = Type.Object(
  {
    businessDate: BusinessDateSchema,
    employeeId: StableIdSchema,
    role: WorkforceAttendanceRoleSchema,
    plannedStartAt: CanonicalDateTimeSchema,
    plannedEndAt: CanonicalDateTimeSchema,
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type CreateWorkforceScheduleRequest = Static<typeof CreateWorkforceScheduleRequestSchema>;

export const WorkforceAttendanceClockRequestSchema = Type.Object(
  {
    action: Type.Union([Type.Literal("CHECK_IN"), Type.Literal("CHECK_OUT")]),
    expectedScheduleVersion: VersionSchema,
    expectedAttendanceVersion: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type WorkforceAttendanceClockRequest = Static<typeof WorkforceAttendanceClockRequestSchema>;

export const RequestWorkforceAttendanceCorrectionSchema = Type.Object(
  {
    expectedScheduleVersion: VersionSchema,
    expectedAttendanceVersion: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    requestedCheckedInAt: CanonicalDateTimeSchema,
    requestedCheckedOutAt: Type.Union([CanonicalDateTimeSchema, Type.Null()]),
    reason: ReasonSchema,
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type RequestWorkforceAttendanceCorrection = Static<
  typeof RequestWorkforceAttendanceCorrectionSchema
>;

export const DecideWorkforceAttendanceCorrectionSchema = Type.Object(
  {
    decision: Type.Union([Type.Literal("APPROVE"), Type.Literal("REJECT")]),
    expectedCorrectionVersion: VersionSchema,
    expectedAttendanceVersion: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    note: ReasonSchema,
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type DecideWorkforceAttendanceCorrection = Static<
  typeof DecideWorkforceAttendanceCorrectionSchema
>;

export const AcknowledgeWorkforceAttendanceCorrectionSchema = Type.Object(
  {
    expectedCorrectionVersion: VersionSchema,
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type AcknowledgeWorkforceAttendanceCorrection = Static<
  typeof AcknowledgeWorkforceAttendanceCorrectionSchema
>;

export const RequestWorkforceLeaveSchema = Type.Object(
  {
    expectedScheduleVersion: VersionSchema,
    expectedItemRevision: VersionSchema,
    reason: ReasonSchema,
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type RequestWorkforceLeave = Static<typeof RequestWorkforceLeaveSchema>;

export const DecideWorkforceLeaveSchema = Type.Object(
  {
    decision: Type.Union([Type.Literal("APPROVE"), Type.Literal("REJECT")]),
    expectedLeaveRequestVersion: VersionSchema,
    expectedItemRevision: VersionSchema,
    note: ReasonSchema,
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type DecideWorkforceLeave = Static<typeof DecideWorkforceLeaveSchema>;

export const AcknowledgeWorkforceLeaveSchema = Type.Object(
  {
    expectedLeaveRequestVersion: VersionSchema,
    expectedItemRevision: VersionSchema,
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type AcknowledgeWorkforceLeave = Static<typeof AcknowledgeWorkforceLeaveSchema>;

export const WorkforceAttendanceMutationResultSchema = Type.Object(
  {
    operation: Type.Union([
      Type.Literal("SCHEDULE_CREATED"),
      Type.Literal("CHECKED_IN"),
      Type.Literal("CHECKED_OUT"),
      Type.Literal("CORRECTION_REQUESTED"),
      Type.Literal("CORRECTION_APPROVED"),
      Type.Literal("CORRECTION_REJECTED"),
      Type.Literal("CORRECTION_ACKNOWLEDGED"),
      Type.Literal("LEAVE_REQUESTED"),
      Type.Literal("LEAVE_APPROVED"),
      Type.Literal("LEAVE_REJECTED"),
      Type.Literal("LEAVE_ACKNOWLEDGED"),
    ]),
    actorAccountId: StableIdSchema,
    actorEmployeeId: StableIdSchema,
    item: WorkforceAttendanceWorkspaceItemSchema,
  },
  { additionalProperties: false },
);
export type WorkforceAttendanceMutationResult = Static<
  typeof WorkforceAttendanceMutationResultSchema
>;

export const WorkforceAttendanceBusinessEventPayloadSchema = Type.Object(
  {
    operation: WorkforceAttendanceMutationResultSchema.properties.operation,
    actorAccountId: StableIdSchema,
    actorEmployeeId: StableIdSchema,
    item: WorkforceAttendanceWorkspaceItemSchema,
  },
  { additionalProperties: false },
);
export type WorkforceAttendanceBusinessEventPayload = Static<
  typeof WorkforceAttendanceBusinessEventPayloadSchema
>;

function canonicalEpoch(value: string): number | null {
  const epoch = Date.parse(value);
  return Number.isFinite(epoch) && new Date(epoch).toISOString() === value ? epoch : null;
}

function isRealBusinessDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/u.test(value)) return false;
  const epoch = Date.parse(`${value}T00:00:00.000Z`);
  return Number.isFinite(epoch) && new Date(epoch).toISOString() === `${value}T00:00:00.000Z`;
}

function previousBusinessDate(value: string): string | null {
  if (!isRealBusinessDate(value)) return null;
  const date = new Date(`${value}T00:00:00.000Z`);
  date.setUTCDate(date.getUTCDate() - 1);
  return date.toISOString().slice(0, 10);
}

export function deriveWorkforceAttendanceBusinessDate(
  occurredAt: string,
  timeZone: string,
  businessDayCutover: string,
): string | null {
  if (
    canonicalEpoch(occurredAt) === null ||
    !new RegExp(BUSINESS_DAY_CUTOVER_PATTERN, "u").test(businessDayCutover)
  ) {
    return null;
  }
  try {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    }).formatToParts(new Date(occurredAt));
    const part = (type: Intl.DateTimeFormatPartTypes): string | undefined =>
      parts.find((candidate) => candidate.type === type)?.value;
    const year = part("year");
    const month = part("month");
    const day = part("day");
    const hour = Number(part("hour"));
    const minute = Number(part("minute"));
    if (!year || !month || !day || !Number.isInteger(hour) || !Number.isInteger(minute)) {
      return null;
    }
    const localDate = `${year}-${month}-${day}`;
    const [cutoverHour, cutoverMinute] = businessDayCutover.split(":").map(Number);
    const cutoverMinuteOfDay = (cutoverHour ?? 0) * 60 + (cutoverMinute ?? 0);
    return hour * 60 + minute < cutoverMinuteOfDay ? previousBusinessDate(localDate) : localDate;
  } catch {
    return null;
  }
}

function deriveFortyEightBitHex(value: string): string {
  let hash = FNV_1A_64_OFFSET;
  for (const character of value) {
    hash ^= BigInt(character.codePointAt(0) ?? 0);
    hash = BigInt.asUintN(64, hash * FNV_1A_64_PRIME);
  }
  return (hash & LOWER_48_BITS).toString(16).toUpperCase().padStart(12, "0");
}

function deriveBusinessNumber(
  prefix: "PB" | "KQ" | "BK" | "QJ",
  businessDate: string,
  sourceId: string,
): string {
  if (!isRealBusinessDate(businessDate)) {
    throw new TypeError("businessDate must be a real calendar date in YYYY-MM-DD format");
  }
  if (
    !BUSINESS_NUMBER_SOURCE_PATTERN.test(sourceId) ||
    [...sourceId].filter((character) => /[A-Za-z0-9]/u.test(character)).length < 12
  ) {
    throw new TypeError("sourceId must contain at least 12 safe alphanumeric characters");
  }
  return `${prefix}-${businessDate.replaceAll("-", "")}-${deriveFortyEightBitHex(
    `${prefix}\0${businessDate}\0${sourceId}`,
  )}`;
}

export function deriveWorkforceScheduleNumber(
  businessDate: string,
  scheduleId: string,
): WorkforceScheduleNumber {
  return deriveBusinessNumber("PB", businessDate, scheduleId) as WorkforceScheduleNumber;
}

export function deriveWorkforceAttendanceNumber(
  businessDate: string,
  attendanceId: string,
): WorkforceAttendanceNumber {
  return deriveBusinessNumber("KQ", businessDate, attendanceId) as WorkforceAttendanceNumber;
}

export function deriveWorkforceAttendanceCorrectionNumber(
  businessDate: string,
  correctionId: string,
): WorkforceAttendanceCorrectionNumber {
  return deriveBusinessNumber(
    "BK",
    businessDate,
    correctionId,
  ) as WorkforceAttendanceCorrectionNumber;
}

export function deriveWorkforceLeaveRequestNumber(
  businessDate: string,
  leaveRequestId: string,
): WorkforceLeaveRequestNumber {
  return deriveBusinessNumber("QJ", businessDate, leaveRequestId) as WorkforceLeaveRequestNumber;
}

function businessNumberDate(value: string, pattern: RegExp): string | null {
  const match = pattern.exec(value);
  if (!match) return null;
  const businessDate = `${match[1]}-${match[2]}-${match[3]}`;
  return isRealBusinessDate(businessDate) ? businessDate : null;
}

export function isWorkforceSchedule(value: unknown): value is WorkforceSchedule {
  if (!Value.Check(WorkforceScheduleSchema, value)) return false;
  const start = canonicalEpoch(value.plannedStartAt);
  const end = canonicalEpoch(value.plannedEndAt);
  const created = canonicalEpoch(value.createdAt);
  const updated = canonicalEpoch(value.updatedAt);
  let expectedNumber: WorkforceScheduleNumber;
  try {
    expectedNumber = deriveWorkforceScheduleNumber(value.businessDate, value.scheduleId);
  } catch {
    return false;
  }
  return (
    start !== null &&
    end !== null &&
    created !== null &&
    updated !== null &&
    end > start &&
    end - start <= 24 * 60 * 60_000 &&
    updated >= created &&
    deriveWorkforceAttendanceBusinessDate(
      value.plannedStartAt,
      value.timeZone,
      value.businessDayCutover,
    ) === value.businessDate &&
    value.scheduleNumber === expectedNumber &&
    businessNumberDate(value.scheduleNumber, SCHEDULE_NUMBER_PATTERN) === value.businessDate &&
    value.employeeNumber === value.employeeNumber.trim() &&
    value.employeeName === value.employeeName.trim() &&
    ((value.state === "SCHEDULED" && value.cancellationReason === null) ||
      (value.state === "CANCELLED" &&
        value.cancellationReason !== null &&
        value.cancellationReason === value.cancellationReason.trim()))
  );
}

export function deriveWorkforceAttendanceDurations(input: {
  plannedStartAt: string;
  plannedEndAt: string;
  checkedInAt: string;
  checkedOutAt: string | null;
}): { lateMinutes: number; earlyLeaveMinutes: number | null; overtimeMinutes: number | null } {
  const plannedStart = canonicalEpoch(input.plannedStartAt);
  const plannedEnd = canonicalEpoch(input.plannedEndAt);
  const checkedIn = canonicalEpoch(input.checkedInAt);
  const checkedOut = input.checkedOutAt === null ? null : canonicalEpoch(input.checkedOutAt);
  if (
    plannedStart === null ||
    plannedEnd === null ||
    checkedIn === null ||
    (input.checkedOutAt !== null && checkedOut === null)
  ) {
    throw new TypeError("attendance timestamps must be canonical ISO timestamps");
  }
  return {
    lateMinutes: Math.min(1_440, Math.max(0, Math.ceil((checkedIn - plannedStart) / 60_000))),
    earlyLeaveMinutes:
      checkedOut === null
        ? null
        : Math.min(1_440, Math.max(0, Math.ceil((plannedEnd - checkedOut) / 60_000))),
    overtimeMinutes:
      checkedOut === null
        ? null
        : Math.min(1_440, Math.max(0, Math.ceil((checkedOut - plannedEnd) / 60_000))),
  };
}

export function workforceAttendanceCorrectionFitsSchedule(
  schedule: WorkforceSchedule,
  checkedInAt: string,
  checkedOutAt: string | null,
): boolean {
  if (schedule.state !== "SCHEDULED") return false;
  const plannedStart = canonicalEpoch(schedule.plannedStartAt);
  const plannedEnd = canonicalEpoch(schedule.plannedEndAt);
  const checkedIn = canonicalEpoch(checkedInAt);
  const checkedOut = checkedOutAt === null ? null : canonicalEpoch(checkedOutAt);
  if (
    plannedStart === null ||
    plannedEnd === null ||
    checkedIn === null ||
    (checkedOutAt !== null && checkedOut === null)
  ) {
    return false;
  }
  const allowedBusinessDates = new Set([
    deriveWorkforceAttendanceBusinessDate(
      schedule.plannedStartAt,
      schedule.timeZone,
      schedule.businessDayCutover,
    ),
    deriveWorkforceAttendanceBusinessDate(
      new Date(plannedEnd - 1).toISOString(),
      schedule.timeZone,
      schedule.businessDayCutover,
    ),
  ]);
  const checkedInBusinessDate = deriveWorkforceAttendanceBusinessDate(
    checkedInAt,
    schedule.timeZone,
    schedule.businessDayCutover,
  );
  const checkedOutBusinessDate =
    checkedOut === null
      ? null
      : deriveWorkforceAttendanceBusinessDate(
          new Date(checkedOut - 1).toISOString(),
          schedule.timeZone,
          schedule.businessDayCutover,
        );
  if (
    checkedInBusinessDate === null ||
    !allowedBusinessDates.has(checkedInBusinessDate) ||
    (checkedOutBusinessDate !== null && !allowedBusinessDates.has(checkedOutBusinessDate))
  ) {
    return false;
  }
  if (checkedOut === null) return checkedIn >= plannedStart && checkedIn < plannedEnd;
  return (
    checkedOut > checkedIn &&
    checkedOut - checkedIn <= 24 * 60 * 60_000 &&
    checkedIn < plannedEnd &&
    checkedOut > plannedStart
  );
}

export function isWorkforceAttendanceRecord(
  value: unknown,
  schedule: WorkforceSchedule,
): value is WorkforceAttendanceRecord {
  if (!Value.Check(WorkforceAttendanceRecordSchema, value) || schedule.state !== "SCHEDULED") {
    return false;
  }
  const checkedIn = canonicalEpoch(value.checkedInAt);
  const checkedOut = value.checkedOutAt === null ? null : canonicalEpoch(value.checkedOutAt);
  const created = canonicalEpoch(value.createdAt);
  const updated = canonicalEpoch(value.updatedAt);
  if (
    checkedIn === null ||
    created === null ||
    updated === null ||
    (value.checkedOutAt !== null && checkedOut === null) ||
    (checkedOut !== null && checkedOut <= checkedIn) ||
    updated < created ||
    value.scheduleId !== schedule.scheduleId ||
    value.scheduleNumber !== schedule.scheduleNumber ||
    value.employeeId !== schedule.employeeId ||
    businessNumberDate(value.attendanceNumber, ATTENDANCE_NUMBER_PATTERN) !== schedule.businessDate
  ) {
    return false;
  }
  let expectedNumber: WorkforceAttendanceNumber;
  try {
    expectedNumber = deriveWorkforceAttendanceNumber(schedule.businessDate, value.attendanceId);
  } catch {
    return false;
  }
  if (value.attendanceNumber !== expectedNumber) return false;
  const durations = deriveWorkforceAttendanceDurations({
    plannedStartAt: schedule.plannedStartAt,
    plannedEndAt: schedule.plannedEndAt,
    checkedInAt: value.checkedInAt,
    checkedOutAt: value.checkedOutAt,
  });
  return (
    durations.lateMinutes === value.lateMinutes &&
    durations.earlyLeaveMinutes === value.earlyLeaveMinutes &&
    durations.overtimeMinutes === value.overtimeMinutes &&
    ((value.state === "WORKING" && value.checkedOutAt === null) ||
      (value.state === "COMPLETED" && value.checkedOutAt !== null))
  );
}

export function isWorkforceAttendanceCorrection(
  value: unknown,
  schedule: WorkforceSchedule,
  attendance: WorkforceAttendanceRecord | null,
): value is WorkforceAttendanceCorrection {
  if (!Value.Check(WorkforceAttendanceCorrectionSchema, value)) return false;
  const requestedIn = canonicalEpoch(value.requestedCheckedInAt);
  const requestedOut =
    value.requestedCheckedOutAt === null ? null : canonicalEpoch(value.requestedCheckedOutAt);
  const requestedAt = canonicalEpoch(value.requestedAt);
  const decidedAt = value.decidedAt === null ? null : canonicalEpoch(value.decidedAt);
  const acknowledgedAt =
    value.acknowledgedAt === null ? null : canonicalEpoch(value.acknowledgedAt);
  if (
    requestedIn === null ||
    requestedAt === null ||
    (value.requestedCheckedOutAt !== null && requestedOut === null) ||
    (requestedOut !== null && requestedOut <= requestedIn) ||
    (value.decidedAt !== null && decidedAt === null) ||
    (value.acknowledgedAt !== null && acknowledgedAt === null) ||
    value.scheduleId !== schedule.scheduleId ||
    value.scheduleNumber !== schedule.scheduleNumber ||
    value.employeeId !== schedule.employeeId ||
    value.attendanceId !== (attendance?.attendanceId ?? null) ||
    value.requestedBy !== schedule.employeeId ||
    value.requestReason !== value.requestReason.trim() ||
    businessNumberDate(value.correctionNumber, CORRECTION_NUMBER_PATTERN) !== schedule.businessDate
  ) {
    return false;
  }
  if (
    !workforceAttendanceCorrectionFitsSchedule(
      schedule,
      value.requestedCheckedInAt,
      value.requestedCheckedOutAt,
    )
  ) {
    return false;
  }
  let expectedNumber: WorkforceAttendanceCorrectionNumber;
  try {
    expectedNumber = deriveWorkforceAttendanceCorrectionNumber(
      schedule.businessDate,
      value.correctionId,
    );
  } catch {
    return false;
  }
  if (value.correctionNumber !== expectedNumber) return false;
  if (value.state === "PENDING") {
    return (
      value.decisionNote === null &&
      value.decision === null &&
      value.decidedBy === null &&
      value.decidedAt === null &&
      value.acknowledgedAt === null &&
      value.createdAttendanceVersion === null
    );
  }
  if (
    value.decision === null ||
    value.decisionNote === null ||
    value.decisionNote !== value.decisionNote.trim() ||
    value.decidedBy === null ||
    decidedAt === null ||
    decidedAt < requestedAt
  ) {
    return false;
  }
  if (
    (value.decision === "REJECTED" && value.createdAttendanceVersion !== null) ||
    (value.decision === "APPROVED" &&
      value.createdAttendanceVersion !== attendance?.attendanceVersion)
  ) {
    return false;
  }
  if (value.state === "DECIDED") return value.acknowledgedAt === null;
  return acknowledgedAt !== null && acknowledgedAt >= decidedAt;
}

export function isWorkforceLeaveRequest(
  value: unknown,
  schedule: WorkforceSchedule,
): value is WorkforceLeaveRequest {
  if (!Value.Check(WorkforceLeaveRequestSchema, value) || schedule.state !== "SCHEDULED") {
    return false;
  }
  const requestedAt = canonicalEpoch(value.requestedAt);
  const decidedAt = value.decidedAt === null ? null : canonicalEpoch(value.decidedAt);
  const acknowledgedAt =
    value.acknowledgedAt === null ? null : canonicalEpoch(value.acknowledgedAt);
  if (
    requestedAt === null ||
    (value.decidedAt !== null && decidedAt === null) ||
    (value.acknowledgedAt !== null && acknowledgedAt === null) ||
    value.scheduleId !== schedule.scheduleId ||
    value.scheduleNumber !== schedule.scheduleNumber ||
    value.employeeId !== schedule.employeeId ||
    value.requestedBy !== schedule.employeeId ||
    value.requestReason !== value.requestReason.trim() ||
    businessNumberDate(value.leaveRequestNumber, LEAVE_REQUEST_NUMBER_PATTERN) !==
      schedule.businessDate
  ) {
    return false;
  }
  let expectedNumber: WorkforceLeaveRequestNumber;
  try {
    expectedNumber = deriveWorkforceLeaveRequestNumber(schedule.businessDate, value.leaveRequestId);
  } catch {
    return false;
  }
  if (value.leaveRequestNumber !== expectedNumber) return false;
  if (value.state === "PENDING") {
    return (
      value.decision === null &&
      value.decisionNote === null &&
      value.decidedBy === null &&
      value.decidedAt === null &&
      value.acknowledgedAt === null
    );
  }
  if (
    value.decision === null ||
    value.decisionNote === null ||
    value.decisionNote !== value.decisionNote.trim() ||
    value.decidedBy === null ||
    decidedAt === null ||
    decidedAt < requestedAt
  ) {
    return false;
  }
  if (value.state === "DECIDED") return value.acknowledgedAt === null;
  return acknowledgedAt !== null && acknowledgedAt >= decidedAt;
}

export function isWorkforceAttendanceWorkspaceItem(
  value: unknown,
): value is WorkforceAttendanceWorkspaceItem {
  if (!Value.Check(WorkforceAttendanceWorkspaceItemSchema, value)) return false;
  if (!isWorkforceSchedule(value.schedule)) return false;
  const latestLeaveRequest = value.latestLeaveRequest ?? null;
  if (
    value.schedule.state === "CANCELLED" &&
    (value.attendance !== null || value.latestCorrection !== null || latestLeaveRequest !== null)
  ) {
    return false;
  }
  if (value.attendance !== null && !isWorkforceAttendanceRecord(value.attendance, value.schedule)) {
    return false;
  }
  if (
    value.latestCorrection !== null &&
    !isWorkforceAttendanceCorrection(value.latestCorrection, value.schedule, value.attendance)
  ) {
    return false;
  }
  if (
    value.latestCorrection !== null &&
    value.latestCorrection.createdAttendanceVersion !== null &&
    value.attendance?.lastCorrectionNumber !== value.latestCorrection.correctionNumber
  ) {
    return false;
  }
  if (latestLeaveRequest !== null && !isWorkforceLeaveRequest(latestLeaveRequest, value.schedule)) {
    return false;
  }
  if (
    latestLeaveRequest !== null &&
    (latestLeaveRequest.state !== "ACKNOWLEDGED" || latestLeaveRequest.decision === "APPROVED") &&
    (value.attendance !== null || value.latestCorrection !== null)
  ) {
    return false;
  }
  return true;
}

export function isWorkforceAttendanceWorkspace(
  value: unknown,
  scope?: { tenantId: string; storeId: string },
): value is WorkforceAttendanceWorkspace {
  if (!Value.Check(WorkforceAttendanceWorkspaceSchema, value)) return false;
  if (
    !isRealBusinessDate(value.businessDate) ||
    canonicalEpoch(value.generatedAt) === null ||
    (scope && (value.tenantId !== scope.tenantId || value.storeId !== scope.storeId)) ||
    (value.viewer === "SELF") !== (value.viewerEmployeeId !== null)
  ) {
    return false;
  }
  try {
    new Intl.DateTimeFormat("zh-CN", { timeZone: value.timeZone }).format(new Date(0));
  } catch {
    return false;
  }
  const scheduleIds = new Set<string>();
  let previousStart = Number.NEGATIVE_INFINITY;
  for (const item of value.items) {
    if (
      !isWorkforceAttendanceWorkspaceItem(item) ||
      item.schedule.tenantId !== value.tenantId ||
      item.schedule.storeId !== value.storeId ||
      item.schedule.businessDate !== value.businessDate ||
      item.schedule.timeZone !== value.timeZone ||
      item.schedule.businessDayCutover !== value.businessDayCutover ||
      (value.viewer === "SELF" && item.schedule.employeeId !== value.viewerEmployeeId) ||
      scheduleIds.has(item.schedule.scheduleId)
    ) {
      return false;
    }
    const plannedStart = Date.parse(item.schedule.plannedStartAt);
    if (plannedStart < previousStart) return false;
    previousStart = plannedStart;
    scheduleIds.add(item.schedule.scheduleId);
  }
  return true;
}

export function deriveWorkforceAttendanceOutcome(
  item: WorkforceAttendanceWorkspaceItem,
  authoritativeAt: string,
): WorkforceAttendanceOutcome | null {
  if (!isWorkforceAttendanceWorkspaceItem(item)) return null;
  const authoritativeEpoch = canonicalEpoch(authoritativeAt);
  const plannedEndEpoch = canonicalEpoch(item.schedule.plannedEndAt);
  if (authoritativeEpoch === null || plannedEndEpoch === null) return null;
  if (item.schedule.state === "CANCELLED") return "CANCELLED";
  if (item.attendance?.state === "WORKING") return "WORKING";
  if (item.attendance?.state === "COMPLETED") return "COMPLETED";
  const leaveRequest = item.latestLeaveRequest ?? null;
  if (leaveRequest?.state === "PENDING") return "LEAVE_PENDING";
  if (leaveRequest?.state === "DECIDED") {
    return leaveRequest.decision === "APPROVED"
      ? "LEAVE_APPROVED_AWAITING_ACKNOWLEDGEMENT"
      : "LEAVE_REJECTED_AWAITING_ACKNOWLEDGEMENT";
  }
  if (leaveRequest?.decision === "APPROVED") return "LEAVE_APPROVED";
  return authoritativeEpoch >= plannedEndEpoch ? "ABSENT" : "SCHEDULED";
}

export function isCreateWorkforceScheduleRequest(
  value: unknown,
): value is CreateWorkforceScheduleRequest {
  if (!Value.Check(CreateWorkforceScheduleRequestSchema, value)) return false;
  const start = canonicalEpoch(value.plannedStartAt);
  const end = canonicalEpoch(value.plannedEndAt);
  return (
    isRealBusinessDate(value.businessDate) &&
    start !== null &&
    end !== null &&
    end > start &&
    end - start <= 24 * 60 * 60_000
  );
}

export function isWorkforceAttendanceClockRequest(
  value: unknown,
): value is WorkforceAttendanceClockRequest {
  return Value.Check(WorkforceAttendanceClockRequestSchema, value);
}

export function isRequestWorkforceAttendanceCorrection(
  value: unknown,
): value is RequestWorkforceAttendanceCorrection {
  if (!Value.Check(RequestWorkforceAttendanceCorrectionSchema, value)) return false;
  const checkedIn = canonicalEpoch(value.requestedCheckedInAt);
  const checkedOut =
    value.requestedCheckedOutAt === null ? null : canonicalEpoch(value.requestedCheckedOutAt);
  return (
    checkedIn !== null &&
    (value.requestedCheckedOutAt === null || (checkedOut !== null && checkedOut > checkedIn)) &&
    value.reason === value.reason.trim()
  );
}

export function isDecideWorkforceAttendanceCorrection(
  value: unknown,
): value is DecideWorkforceAttendanceCorrection {
  return (
    Value.Check(DecideWorkforceAttendanceCorrectionSchema, value) &&
    value.note === value.note.trim()
  );
}

export function isAcknowledgeWorkforceAttendanceCorrection(
  value: unknown,
): value is AcknowledgeWorkforceAttendanceCorrection {
  return Value.Check(AcknowledgeWorkforceAttendanceCorrectionSchema, value);
}

export function isRequestWorkforceLeave(value: unknown): value is RequestWorkforceLeave {
  return Value.Check(RequestWorkforceLeaveSchema, value) && value.reason === value.reason.trim();
}

export function isDecideWorkforceLeave(value: unknown): value is DecideWorkforceLeave {
  return Value.Check(DecideWorkforceLeaveSchema, value) && value.note === value.note.trim();
}

export function isAcknowledgeWorkforceLeave(value: unknown): value is AcknowledgeWorkforceLeave {
  return Value.Check(AcknowledgeWorkforceLeaveSchema, value);
}

export function isWorkforceAttendanceMutationResult(
  value: unknown,
): value is WorkforceAttendanceMutationResult {
  if (
    !Value.Check(WorkforceAttendanceMutationResultSchema, value) ||
    !isWorkforceAttendanceWorkspaceItem(value.item)
  ) {
    return false;
  }
  if (value.operation === "SCHEDULE_CREATED") {
    return value.item.schedule.createdBy === value.actorAccountId;
  }
  if (
    value.operation === "CHECKED_IN" ||
    value.operation === "CHECKED_OUT" ||
    value.operation === "CORRECTION_REQUESTED" ||
    value.operation === "CORRECTION_ACKNOWLEDGED" ||
    value.operation === "LEAVE_REQUESTED" ||
    value.operation === "LEAVE_ACKNOWLEDGED"
  ) {
    return value.item.schedule.employeeId === value.actorEmployeeId;
  }
  if (value.operation === "CORRECTION_APPROVED" || value.operation === "CORRECTION_REJECTED") {
    return value.item.latestCorrection?.decidedBy === value.actorAccountId;
  }
  return value.item.latestLeaveRequest?.decidedBy === value.actorAccountId;
}
